import { Component, ComponentChild, AnyComponent } from 'preact'
import qs from 'qs'
import axios, { AxiosError, AxiosResponse } from 'axios'
import Header from './Header'
import Loader from '../ui-components/Loader'
import OverviewFilters from './filters/OverviewFilters'
import OverviewColumns, { copyColumns } from './columns/OverviewColumns'
import Table from '../../preact-components/table/Table'
import AlertComponent from '../ui-components/AlertComponent'
import copyToClipboard from '../../preact-components/utils/copyToClipboard'
import tracker from '../../components/tracker/publish'
import { Model } from '@insided/deprecated-preact-components/table/types'
import Icon from '../../preact-components/assets'
import Button from '@insided/control/uikit/Button'
import { CustomButton } from '../header'
import { csrfToken } from '@insided/deprecated-preact-components/utils/csrfToken'
import CustomViewActions from './custom-views/CustomViewActions'
import { FilterList } from '@insided/deprecated-control/overview/filters/FiltersMenu'
import objectKeys from '@insided/shared/utils/objectKeys'
import EmptyView from '@insided/deprecated-control/overview/EmptyView'
import { getPageAfterBulkAction } from './utils'
import { subscribe } from '@insided/deprecated-utils/globals'
import { FetchUsersIdsProps } from '../EmailCampaigns/components/Metrics'

export type Filter = any[] | string | any

interface SharedView {
  filters: Filters
  sort: Sort
  columns: ViewColumn[]
}
export interface Filters {
  [filter: string]: Filter
}

export interface BulkAction {
  onClick: (data: []) => Promise<any>
  successMessage: string
}

export interface BulkActionComponentProps {
  numberOfSelectedItems: number
  performBulkAction: (bulkAction: BulkAction) => void
  type: string
}

export interface DataSourceResponse<T> {
  data: T[]
  total: number
}

interface ViewColumn {
  accessor: string
  isHidden: boolean
}

export interface Sort {
  [key: string]: string
}

export interface DataSourceOpts {
  pagination: Pagination
  filters?: Filters
  sort?: Sort
}

interface Props<T> {
  type: string
  headerTitle: string
  onTitleChanged?: (newValue: string) => void
  headerButtons?: Array<CustomButton | ComponentChild>
  headerTitleLinks?: JSX.Element
  withFilters?: FilterList
  showFilters?: boolean
  withHeader?: boolean
  withActionElements?: boolean
  withSearch?: boolean
  searchPlaceholder?: string
  withSorting?: Sort
  withPagination?: {
    page: number
    pageSize: number
    pageSizes?: number[]
  }
  withColumns?: boolean
  rowId: string
  endpoint?: string
  onPaginationAction?: (({ page, pageSize, callback }: FetchUsersIdsProps) => void) | null

  dataSource?: ({ pagination, filters, sort }: DataSourceOpts) => Promise<DataSourceResponse<T>>
  clientSideFilter?: (data: T[], filter: Filter, sort?: Sort) => DataSourceResponse<T>
  dataKey?: string
  model: Model[]
  EmptyStateComponent: JSX.Element
  // [Preact X] Change AnyComponent to ComponentType
  bulkActions?: Array<AnyComponent<BulkActionComponentProps>>
  isBeta?: boolean
  withCustomViews?: string
  message?: JSX.Element
  entity?: string
  withMultiSelect?: boolean
  areAppliedFiltersHiddenByDefault?: boolean
  defaultFilteringOptions?: Filters
  // [Preact X] Change AnyComponent to ComponentType
  StateAwareHeaderComponent?: AnyComponent<StateAwareHeaderComponentProps<T>>
  // [Preact X] Change AnyComponent to ComponentType
  StateAwareDashboardComponent?: AnyComponent<StateAwareHeaderComponentProps<T>>
}

interface StateToPropsBridge<T> {
  data: T[]
  total?: number
  loading: boolean
  areFiltersApplied: boolean
  filters?: Filters
  pagination?: Pagination
  sort?: Sort
}

export interface StateAwareHeaderComponentProps<T> extends StateToPropsBridge<T> {}
export interface StateAwareDashboardComponentProps<T> extends StateToPropsBridge<T> {}

export interface OverviewState<T> extends StateToPropsBridge<T> {
  pagination?: Pagination
  sort?: Sort
  columns: Model[]
  [key: string]: any
  customViewTitle: string
  customViewError: string
  showAlert: boolean
  alertMessage: string
  alertType: string | null
  alertVisibleFor: number | null
  customViewId?: number
  selectedRows: number[]
}

class Overview<T = any> extends Component<Props<T>, OverviewState<T>> {
  private readonly STORAGE_KEY = `${this.props.type}-filter-criteria`
  // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
  private readonly overviewPath = `${
    this.props.withCustomViews === 'default' ? '' : `/${this.props.withCustomViews}`
  }/overview`

  constructor(props: Props<T>) {
    super(props)
    this.state = {
      data: [],
      total: undefined,
      loading: true,
      areFiltersApplied: false,
      ...this.getDefaultFilteringOptions(),
      customViewTitle: '',
      customViewError: '',
      showAlert: false,
      alertMessage: '',
      alertType: null,
      alertVisibleFor: null,
      columns: copyColumns(props.model),
      selectedRows: []
    }

    this.handleResponse = this.handleResponse.bind(this)
    this.onPageChange = this.onPageChange.bind(this)
    this.onPageSizeChange = this.onPageSizeChange.bind(this)
    this.handleFilterChange = this.handleFilterChange.bind(this)
    this.handleClearFilterCriteria = this.handleClearFilterCriteria.bind(this)
    this.handleShareView = this.handleShareView.bind(this)
    this.onSortChange = this.onSortChange.bind(this)
    this.handleSaveCustomView = this.handleSaveCustomView.bind(this)
    this.handleRefresh = this.handleRefresh.bind(this)
    this.handleCustomViewCreateError = this.handleCustomViewCreateError.bind(this)
    this.handleCustomViewCreateSuccess = this.handleCustomViewCreateSuccess.bind(this)
    this.handleCustomViewUpdateSuccess = this.handleCustomViewUpdateSuccess.bind(this)
    this.applySelectedColumns = this.applySelectedColumns.bind(this)
    this.onSelectRow = this.onSelectRow.bind(this)
    this.onSelectAll = this.onSelectAll.bind(this)
    this.performBulkAction = this.performBulkAction.bind(this)
    this.handleDeleteCustomView = this.handleDeleteCustomView.bind(this)
    this.applyClientSideFilters = this.applyClientSideFilters.bind(this)
  }

  getDefaultFilteringOptions(): { [field: string]: {} } {
    const { withFilters, withSearch, withSorting, withPagination, defaultFilteringOptions } = this.props
    const filteringOptions = {}

    if (defaultFilteringOptions) {
      return { ...defaultFilteringOptions }
    }

    if (withFilters) {
      const emptyFilters = {}

      if (withSearch) {
        Object.assign(emptyFilters, { search: '' })
      }

      Object.assign(filteringOptions, { filters: emptyFilters })
    }

    if (withSorting) {
      Object.assign(filteringOptions, { sort: withSorting })
    }

    if (withPagination) {
      const { page, pageSize, pageSizes } = withPagination
      Object.assign(filteringOptions, { pagination: { page, pageSize, pageSizes } })
    }

    return filteringOptions
  }

  applyViewFromStorage() {
    if (!window.sessionStorage.getItem(this.STORAGE_KEY)) {
      window.sessionStorage.setItem(this.STORAGE_KEY, JSON.stringify(this.getDefaultFilteringOptions()))
    } else {
      const {
        filters = {},
        pagination,
        sort,
        columns
      } = JSON.parse(window.sessionStorage.getItem(this.STORAGE_KEY) as string)
      const appliedFilters = Object.fromEntries(Object.entries(filters).filter(([_, value]) => value !== null))
      const areFiltersApplied =
        JSON.stringify(appliedFilters) !== JSON.stringify(this.getDefaultFilteringOptions().filters)
      this.setState({
        filters: appliedFilters,
        pagination: pagination,
        sort: sort,
        areFiltersApplied
      })
      if (columns) this.setState({ columns: this.getColumnsFromView(columns) })
    }
  }

  static getCustomViewId() {
    return parseInt(window.location.pathname.split('/overview/')[1])
  }

  static getSharedView() {
    const search = qs.parse(window.location.search, { ignoreQueryPrefix: true })
    const view = search?.view

    try {
      // @ts-expect-error
      return JSON.parse(window.atob(view))
    } catch (e) {
      return null
    }
  }

  static isCustomView(): boolean {
    return !!Overview.getCustomViewId()
  }

  static isSharedView(): boolean {
    return !!Overview.getSharedView()
  }

  async getView() {
    const customViewId = Overview.getCustomViewId()
    if (customViewId) {
      await this.fetchCustomView(customViewId)
      return
    }

    const sharedView = Overview.getSharedView()
    if (sharedView) {
      this.setSharedView(sharedView)
      return
    }

    this.applyViewFromStorage()
  }

  async componentDidMount() {
    const search = qs.parse(window.location.search, { ignoreQueryPrefix: true })
    if (search?.conversion) {
      this.setState({
        alertMessage: 'We are converting your article. Please refresh again after a few moments.',
        alertType: 'success',
        showAlert: true
      })
    }

    await this.getView()
    await this.fetch()
    subscribe('show_alert', ({ alertType, message }) => {
      this.setState({
        alertMessage: message,
        alertType: alertType,
        alertVisibleFor: 3000,
        showAlert: true
      })
    })
  }

  async fetchCustomView(viewId: number) {
    await axios
      .get(`/view/${viewId}`)
      .then((res) => this.setCustomView(res, viewId))
      .catch(() => {})
  }

  applyClientSideFilters(response: DataSourceResponse<T>): DataSourceResponse<T> {
    const { filters } = this.state
    const { clientSideFilter } = this.props
    if (response && response.data.length > 0 && clientSideFilter) {
      return clientSideFilter(response.data, filters)
    }
    return response
  }

  async fetch() {
    this.setState({ loading: true, selectedRows: [] })
    const { filters } = this.state

    const dynamicFields = {}

    if (filters?.assignedModerator) {
      Object.assign(dynamicFields, {
        assignedModerator: filters?.assignedModerator.map((filter: { value: string }) => filter.value)
      })
    }

    if (filters?.author) {
      Object.assign(dynamicFields, {
        author: filters?.author.map((filter: { value: string }) => filter.value)
      })
    }

    if (filters?.userTag) {
      Object.assign(dynamicFields, {
        userTag: filters?.userTag.map((filter: { value: string }) => filter.value)
      })
    }

    if (filters?.productArea) {
      Object.assign(dynamicFields, {
        productArea: filters?.productArea.map((filter: { id: string }) => filter.id)
      })
    }

    if (filters?.publicTag) {
      Object.assign(dynamicFields, {
        publicTag: filters?.publicTag.map((filter: { name: string }) => filter.name)
      })
    }

    if (filters?.role) {
      Object.assign(dynamicFields, {
        role: filters?.role.map((filter: { name: string }) => filter.name)
      })
    }

    await this.fetchData(dynamicFields, filters)
  }

  async fetchData(dynamicFields: {}, filters?: Filters) {
    const { endpoint, dataSource } = this.props
    const { pagination, sort } = this.state
    try {
      let response
      if (endpoint) {
        const apiResponse = await axios.post(endpoint, {
          pagination,
          sort,
          filters: { ...filters, ...dynamicFields }
        })
        response = {
          data: this.props.dataKey ? apiResponse.data[this.props.dataKey] : [],
          total: apiResponse.data.total || undefined
        }
      } else if (dataSource && pagination) {
        response = await dataSource({ pagination, sort, filters })
      }

      if (response) {
        response = this.applyClientSideFilters(response)
        this.handleResponse(response)
      }

      const isLastPageEmpty = response && pagination && pagination.page > 1 && (pagination.page > Math.ceil(response.total/pagination.pageSize))
      if (isLastPageEmpty) {
        this.onPageChange(pagination.page - 1)
      }
    } catch (err) {
      this.setState({ loading: false })
      // Hide error message on reported, pending, trash-can overview when there is no content
      if (!['report', 'pending', 'trash-can'].includes(this.props.type)) this.showSomethingWentWrong()
    }
  }

  async createCustomView(name: string) {
    const { filters, sort, columns } = this.state
    const data = {
      name,
      criteria: {
        filters: filters,
        sort: sort,
        columns: this.getColumnsForView(columns)
      },
      type: this.props.withCustomViews
    }

    await axios.put('/view', data).then(this.handleCustomViewCreateSuccess).catch(this.handleCustomViewCreateError)
  }

  async updateCustomView() {
    const { customViewId, customViewTitle, filters, sort, columns } = this.state
    const data = {
      name: customViewTitle,
      criteria: {
        filters: filters,
        sort: sort,
        columns: this.getColumnsForView(columns)
      },
      type: this.props.withCustomViews
    }

    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
    await axios
      .post(`/view/${customViewId}`, data)
      .then(this.handleCustomViewUpdateSuccess)
      .catch(() => this.showSomethingWentWrong())
  }

  onPageChange(page: number) {
    const { onPaginationAction } = this.props
    const pageSize = this.state.pagination?.pageSize
    const pageSizes = this.props.withPagination?.pageSizes
    if (onPaginationAction && this.state.pagination?.pageSize) {
      onPaginationAction({ page, pageSize: this.state.pagination?.pageSize, callback: () => {
        this.setState({ ...this.getDefaultFilteringOptions() })
        this.updateFilterCriteria('pagination', { page, pageSize, pageSizes })  
      }})
    } else {
      this.updateFilterCriteria('pagination', { page, pageSize, pageSizes })
    }
  }

  onPageSizeChange(pageSize: number) {
    const { onPaginationAction } = this.props
    const page = this.props.withPagination?.page
    const pageSizes = this.props.withPagination?.pageSizes
    if (onPaginationAction && this.props.withPagination?.page) {
      onPaginationAction({ page: this.props.withPagination?.page, pageSize, callback: () => {
        this.setState({ ...this.getDefaultFilteringOptions() })
        this.updateFilterCriteria('pagination', { page, pageSize, pageSizes })  
      } })
    } else {
      this.updateFilterCriteria('pagination', { page, pageSize, pageSizes })
    }
  }

  onSortChange(order: string) {
    const { sort } = this.state

    const currentOrder = sort && Object.keys(sort).filter((currentOrder) => currentOrder === order)

    if (currentOrder && currentOrder.length === 0) {
      this.setState({ sort: { [order]: 'asc' } })
    }

    const direction = this.state.sort?.[order]

    if (direction === 'desc') this.updateFilterCriteria('sort', { [order]: 'asc' })
    if (direction === 'asc') this.updateFilterCriteria('sort', { [order]: 'desc' })
  }

  handleFilterChange(filter: string, payload: string[] | string | null) {
    const { filters = {} } = this.state
    const appliedFilters = Object.fromEntries(
      Object.entries({ ...filters, [filter]: payload }).filter(([_, value]) => value !== null)
    )
    this.updateFilterCriteria('filters', appliedFilters)
    // always go to the first page when updating filters
    const pageSizes = this.props.withPagination?.pageSizes
    const pagination = {
      page: 1,
      pageSize: this.state.pagination?.pageSize ?? 25,
      pageSizes: pageSizes
    }
    const areFiltersApplied =
      JSON.stringify(appliedFilters) !== JSON.stringify(this.getDefaultFilteringOptions().filters)
    this.setState({ areFiltersApplied, pagination })
    // persist pagination reset
    this.updateFilterCriteria('pagination', pagination)
  }

  handleClearFilterCriteria() {
    if (Overview.isCustomView() || Overview.isSharedView()) {
      this.setState({ ...this.getDefaultFilteringOptions(), areFiltersApplied: false }, async () => {
        await this.fetch()
      })
    } else {
      this.setState({ ...this.getDefaultFilteringOptions(), areFiltersApplied: false }, async () => {
        await this.fetch().then(() => {
          window.sessionStorage.setItem(this.STORAGE_KEY, JSON.stringify(this.getDefaultFilteringOptions()))
        })
      })
    }
  }

  handleRefresh(page: number) {
    this.onPageChange(page)
  }

  handleShareView() {
    const sharableViewUrl = this.getSharableViewUrl()

    copyToClipboard(sharableViewUrl)

    this.setState({
      showAlert: true,
      alertMessage: 'Link copied to clipboard!',
      alertType: 'success',
      alertVisibleFor: 3000
    })

    this.trackCustomViewShared()
  }

  async handleDeleteCustomView() {
    const { customViewId } = this.state
    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
    await axios
      .delete(`/view/${customViewId}`, { data: { csrfToken: csrfToken() } })
      .then(() => window.location.assign(this.overviewPath))
      .catch(() => {
        this.setState({ showDeleteCustomViewModal: false })
        this.showSomethingWentWrong()
      })
  }

  async handleSaveCustomView(newCustomViewName?: string) {
    if (newCustomViewName !== undefined) {
      if (newCustomViewName === '') {
        this.setState({ customViewError: 'Please enter a name for your custom view' })
        return
      }
      if (newCustomViewName.length > 250) {
        this.setState({
          customViewError: 'Maximum length: 250 characters. Please reduce the length of the name of your custom view'
        })
        return
      }
      await this.createCustomView(newCustomViewName)
    } else {
      this.validateCustomViewTitleName()
      if (this.state.alertType === 'error') {
        return
      }
      await this.updateCustomView()
    }
  }

  updateFilterCriteria(key: string, value: {}) {
    if (Overview.isCustomView() || Overview.isSharedView()) {
      this.setState({ [key]: value }, async () => {
        await this.fetch()
      })
    } else {
      const storage = JSON.parse(window.sessionStorage.getItem(this.STORAGE_KEY) as string)
      storage[key] = value
      window.sessionStorage.setItem(this.STORAGE_KEY, JSON.stringify(storage))
      this.setState({ [key]: storage[key] }, async () => {
        await this.fetch()
      })
    }
  }

  handleResponse(response: DataSourceResponse<T>) {
    this.setState({
      loading: false,
      ...response
    })
  }

  handleCustomViewCreateSuccess(response: AxiosResponse) {
    const { data } = response

    if (data === undefined) {
      return
    }

    this.redirectToCustomView(data.viewId)
  }

  handleCustomViewUpdateSuccess() {
    this.redirectToCustomView(this.state.customViewId as number)
  }

  redirectToCustomView(viewId: number) {
    window.location.assign(`${this.overviewPath}/${viewId}?showMessage=success`)
  }

  handleCustomViewCreateError(error: AxiosError) {
    if (error.response && error.response.status === 422) {
      this.setState({ customViewError: 'This custom view name already exists' })
      return
    }

    this.showSomethingWentWrong()
  }

  showSomethingWentWrong() {
    this.setState({
      showAlert: true,
      alertMessage: 'Something went wrong. Please try again',
      alertType: 'error',
      alertVisibleFor: 3000,
      loading: false
    })
  }

  getColumnsFromView(viewColumns: ViewColumn[]) {
    return this.state.columns.map((column) => {
      const viewColumn = viewColumns.find((viewColumn: ViewColumn) => column.accessor === viewColumn.accessor)
      if (viewColumn) column.isHidden = !!viewColumn.isHidden
      return column
    })
  }

  getColumnsForView(columns: Model[]) {
    return columns.map((column) => {
      return { accessor: column.accessor, isHidden: !!column.isHidden }
    })
  }

  setCustomView(response: AxiosResponse, customViewId: number) {
    if (response.data.length === 0) {
      return
    }

    const { name, criteria } = response.data
    const { filters, sort, columns } = criteria

    this.setState({
      customViewId,
      filters: { ...this.state.filters, ...filters },
      sort,
      areFiltersApplied: true,
      customViewTitle: name
    })
    columns && this.setState({ columns: this.getColumnsFromView(columns) })

    if (window.location.search.includes('?showMessage=success')) {
      this.setState({
        showAlert: true,
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        alertMessage: `Custom view ‘${name}’ saved successfully!`,
        alertType: 'success',
        alertVisibleFor: 3000
      })
      window.history.replaceState(null, '', customViewId.toString())
      this.trackCustomViewSaved()
    } else {
      this.trackCustomViewOpened()
    }
  }

  setSharedView(sharedView: SharedView) {
    const { filters, sort, columns } = sharedView

    this.setState({
      filters: filters,
      sort: sort
    })
    columns && this.setState({ columns: this.getColumnsFromView(columns) })
  }

  getViewParameters(): string {
    const { filters, sort, columns } = this.state

    return JSON.stringify({
      filters: filters,
      sort: sort,
      columns: this.getColumnsForView(columns)
    })
  }

  getSharableViewUrl(): string {
    return `${window.location.origin}${this.overviewPath}?view=${window.btoa(this.getViewParameters())}`
  }

  getPaginationProps() {
    const { withPagination } = this.props
    if (!withPagination) return

    const { pagination, total } = this.state

    return {
      pagination: { ...pagination, total },
      onPageChange: this.onPageChange,
      onPageSizeChange: this.onPageSizeChange
    }
  }

  getTableProps() {
    const {
      withPagination,
      withSorting,
      withColumns,
      model,
      rowId,
      dataKey,
      bulkActions,
      entity,
      type,
      withMultiSelect = true,
      withActionElements = true
    } = this.props
    const { data, columns, selectedRows, pagination } = this.state
    const page = pagination?.page

    const tableProps = {
      model: columns,
      rows: data,
      rowId,
      firstColumnSticky: true,
      actionElements: ( withActionElements ? (
        <div className='overview_table-action-elements'>
          <Button
            // eslint-disable-next-line @gorazdo/preact/prefer-classname
            className='refresh-button'
            onClick={() => this.handleRefresh(page ?? 1)}
            size='medium'
            title={
              <div>
                <Icon icon='refresh' height={12} width={12} />
                Refresh
              </div>
            }
          />
          {withColumns && (
            <OverviewColumns
              selectedColumns={columns}
              defaultColumns={model}
              applySelectedColumns={this.applySelectedColumns}
            />
          )}
        </div>
      ) : undefined),
      withMultiSelect: withMultiSelect,
      onSelectRow: this.onSelectRow,
      onSelectAll: this.onSelectAll,
      onClearSelection: () => this.setState({ selectedRows: [] }),
      selectedRows,
      allSelected: selectedRows.length === data.length,
      entity: entity ?? dataKey,
      bulkActions: (
        <div className='overview-bulk-actions'>
          {bulkActions?.map((BulkActionComponent, id) => {
            return (
              <BulkActionComponent
                key={`bulk-action-${id}`}
                numberOfSelectedItems={selectedRows.length}
                performBulkAction={this.performBulkAction}
                type={type}
              />
            )
          })}
        </div>
      )
    }

    if (withSorting) {
      const { sort } = this.state
      sort &&
        Object.keys(sort).forEach((key: string) => {
          Object.assign(tableProps, {
            sort: { order: key, direction: sort[key] },
            onSortChange: this.onSortChange
          })
        })
    }

    if (withPagination) {
      Object.assign(tableProps, { withPagination: this.getPaginationProps() })
    }

    return tableProps
  }

  async performBulkAction({ onClick, successMessage }: BulkAction) {
    this.setState({ loading: true })

    const { data, selectedRows, pagination } = this.state
    const selectedObjects: any = data.filter((item) =>
      // @ts-expect-error
      selectedRows.includes(item[this.props.rowId])
    )

    const isData = data.length - selectedObjects.length >= 0
    const page = getPageAfterBulkAction(pagination, isData)

    this.handleRefresh(page)

    await onClick(selectedObjects)
      .then(async () => {
        await this.fetch()
        this.setState({
          showAlert: true,
          alertMessage: successMessage,
          alertType: 'success',
          alertVisibleFor: 3000
        })
      })
      .catch(() => this.showSomethingWentWrong())
  }

  onSelectRow(rowId: number, checked: boolean) {
    this.setState(({ selectedRows }) => ({
      selectedRows: checked
        ? [...this.state.selectedRows, rowId]
        : selectedRows.filter((selectedRowId: number) => selectedRowId !== rowId)
    }))
  }

  onSelectAll() {
    this.setState(({ selectedRows, data }) => ({
      selectedRows: selectedRows.length === data.length ? [] : data.map((row: any) => row[this.props.rowId])
    }))
  }

  applySelectedColumns(columns: Model[]) {
    this.setState({ columns: columns })

    if (Overview.isCustomView() || Overview.isSharedView()) {
      return
    }

    const storage = JSON.parse(window.sessionStorage.getItem(this.STORAGE_KEY) as string)
    storage.columns = this.getColumnsForView(columns)
    window.sessionStorage.setItem(this.STORAGE_KEY, JSON.stringify(storage))
  }

  validateCustomViewTitleName() {
    const { customViewTitle } = this.state

    if (!customViewTitle) {
      this.setState({
        showAlert: true,
        alertType: 'error',
        alertMessage: 'Please enter a name for your custom view',
        showSaveCustomViewModal: false
      })
    }
  }

  trackCustomViewOpened() {
    tracker.trackEvent('userevent', {
      type: 'Custom View Opened',
      new_overview: true,
      view_type: this.props.withCustomViews,
      parameters: this.getViewParameters()
    })
  }

  trackCustomViewSaved() {
    tracker.trackEvent('userevent', {
      type: 'Custom View Saved',
      view_type: this.props.withCustomViews,
      parameters: this.getViewParameters()
    })
  }

  trackCustomViewShared() {
    tracker.trackEvent('userevent', {
      type: 'Custom View Shared',
      view_type: this.props.withCustomViews,
      parameters: this.getViewParameters()
    })
  }

  renderStateAwareHeaderComponent = () => {
    const { StateAwareHeaderComponent } = this.props
    const { data, total, loading, areFiltersApplied, filters, pagination, sort } = this.state

    return (
      StateAwareHeaderComponent && (
        <StateAwareHeaderComponent
          data={data}
          total={total}
          loading={loading}
          areFiltersApplied={areFiltersApplied}
          filters={filters}
          pagination={pagination}
          sort={sort}
        />
      )
    )
  }

  renderStateAwareDashboardComponent = () => {
    const { StateAwareDashboardComponent } = this.props
    const { data, total, loading, areFiltersApplied, filters } = this.state

    return (
      StateAwareDashboardComponent && (
        <StateAwareDashboardComponent
          data={data}
          total={total}
          loading={loading}
          areFiltersApplied={areFiltersApplied}
          filters={filters}
        />
      )
    )
  }

  onTitleChanged = (title: string) => {
    const { onTitleChanged } = this.props
    onTitleChanged ? onTitleChanged(title) : this.setState({ customViewTitle: title })
  }

  render() {
    const {
      type,
      headerTitle,
      onTitleChanged,
      headerTitleLinks,
      headerButtons,
      withSearch,
      withHeader = true,
      searchPlaceholder,
      withFilters,
      showFilters = true,
      isBeta = false,
      areAppliedFiltersHiddenByDefault,
      withCustomViews,
      message,
      EmptyStateComponent
    } = this.props
    const {
      data,
      filters,
      loading,
      areFiltersApplied,
      showAlert,
      alertMessage = '',
      alertType,
      alertVisibleFor,
      customViewId,
      customViewError,
      customViewTitle
    } = this.state
    const isCustomView = !!customViewId
    const hasData = data.length > 0
    const showNoResultFound = withFilters && (areFiltersApplied || Overview.isSharedView())

    return (
      <div className='overview'>
        {loading && <Loader />}
        {showAlert && (
          <AlertComponent
            message={alertMessage}
            messageType={alertType}
            isVisibleFor={alertVisibleFor}
            sticky
            onTimeoutEnd={() => this.setState({ showAlert: false })}
          />
        )}
        {withHeader && (
          <Header
            type={type}
            title={isCustomView ? customViewTitle : headerTitle}
            isCustomView={!!onTitleChanged || isCustomView}
            onTitleChanged={this.onTitleChanged}
            customButtons={headerButtons}
            isBeta={isBeta}
            titleLinks={headerTitleLinks}
            customViewActions={
              withCustomViews && (data.length > 0 || areFiltersApplied || Overview.isSharedView()) ? (
                <CustomViewActions
                  isCustomView={isCustomView}
                  customViewName={customViewTitle}
                  onShareView={this.handleShareView}
                  onSaveCustomView={this.handleSaveCustomView}
                  onDeleteCustomView={this.handleDeleteCustomView}
                  errorMessage={customViewError}
                />
              ) : (
                this.renderStateAwareHeaderComponent()
              )
            }
          />
        )}
        {message && message}
        {this.renderStateAwareDashboardComponent()}
        {withFilters && showFilters &&
          (data.length > 0 || objectKeys(filters ?? {}).length > 0 || areFiltersApplied || Overview.isSharedView()) && (
            <OverviewFilters
              filters={withFilters}
              appliedFilters={filters}
              onFilterChange={this.handleFilterChange}
              onClearFilterCriteria={this.handleClearFilterCriteria}
              withSearch={withSearch}
              searchPlaceholder={searchPlaceholder}
              areAppliedFiltersHiddenByDefault={areAppliedFiltersHiddenByDefault}
            />
          )}
        {hasData ? (
          <Table {...this.getTableProps()} />
        ) : (
          <EmptyView
            loading={loading}
            EmptyStateComponent={EmptyStateComponent}
            showNoResultFound={showNoResultFound}
          />
        )}
      </div>
    )
  }
}

export default Overview
